import type { WidgetTheme, Feature, Locale } from "../types";
import { FEATURE_LABELS, FEATURE_ICONS } from "../core/feature-selector";
import { generateStyles } from "./styles";
import { t } from "./i18n";

export interface ContainerCallbacks {
  onPanelOpen: (feature?: Feature, ch?: string) => void;
  onPanelClose: () => void;
}

function svgPhone(): string {
  return '<svg viewBox="0 0 24 24" width="24" height="24" fill="none"><path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 01.91-.27 11.36 11.36 0 003.68.59 1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.45.59 3.57a1 1 0 01-.27.9l-2.2 2.32z" fill="white"/></svg>';
}

function svgChat(): string {
  return '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2v10z"/></svg>';
}

function svgBack(): string {
  return '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>';
}

function svgX(): string {
  return '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
}

function svgCallback(): string {
  return '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16.5a2 2 0 01-2 2h-1.5l-3.5 3.5v-3.5H6a2 2 0 01-2-2V6a2 2 0 012-2h13a2 2 0 012 2v10.5z"/><path d="M14 9l-3 3 3 3"/></svg>';
}

function svgBot(): string {
  return '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="3"/><circle cx="9" cy="11" r="1.5" fill="white" stroke="none"/><circle cx="15" cy="11" r="1.5" fill="white" stroke="none"/><line x1="9" y1="16" x2="15" y2="16"/></svg>';
}

const IM_LOGO: Record<string, string> = {
  whatsapp: '/imlogo/whatsapp.svg', messenger: '/imlogo/messenger.svg',
  instagram: '/imlogo/instagram.svg', telegram: '/imlogo/telegram.svg',
  line: '/imlogo/line.svg', wechat: '/imlogo/wechat.svg',
  zalo: '/imlogo/Zalo.svg', viber: '/imlogo/viber.svg',
};

let _shadowRoot: ShadowRoot | null = null;
let _theme: WidgetTheme | null = null;
let _locale: Locale = "zh-CN";
let _activeFeature: Feature | null = null;
let _features: Feature[] = [];
let _imChannels: Array<{ id: string; name: string; icon: string }> = [];
let _callbacks: ContainerCallbacks | null = null;
let _panelOpen = false;

function $(sel: string): HTMLElement | null {
  return _shadowRoot?.querySelector(sel) as HTMLElement | null;
}

export function setFloatingIcons(features: Feature[], imChannels: Array<{ id: string; name: string; icon: string }>): void {
  _features = features;
  _imChannels = imChannels;
  const iconsContainer = $("#oc-icons");
  const fab = $("#oc-fab");
  if (!iconsContainer) return;
  const c = _theme?.primaryColor || "#533afd";
  let html = "";
  if (features.includes("voice")) html += '<button class="oc-fab-icon pulse" data-feature="voice" style="background:' + c + '" title="AI 语音接待">' + svgPhone() + '</button>';
  if (features.includes("callback")) html += '<button class="oc-fab-icon" data-feature="callback" style="background:' + c + '" title="回拨电话">' + svgCallback() + '</button>';
  if (features.includes("im") && imChannels.length > 0) {
    const channelColors: Record<string, string> = { whatsapp: "#25D366", messenger: "#0084FF", instagram: "#E4405F", telegram: "#26A5E4", line: "#00B900", wechat: "#07C160", zalo: "#0068FF", viber: "#7360F2" };
    for (const ch of imChannels) {
      const col = channelColors[ch.id] || c;
      const logoSrc = IM_LOGO[ch.id] || '';
      html += '<button class="oc-fab-icon oc-im-icon" data-feature="im" data-ch="' + ch.id + '" style="background:#fff;border:2px solid ' + col + '" title="' + ch.name + '">' +
        (logoSrc ? '<img src="' + logoSrc + '" alt="' + ch.name + '" style="width:20px;height:20px;display:block"/>' : '<span style="width:24px;height:24px;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:12px">' + ch.name.charAt(0) + '</span>') + '</button>';
    }
  }
  const hasFloatingIcons = html.length > 0;
  if (fab) fab.style.display = hasFloatingIcons ? "none" : "flex";
  iconsContainer.innerHTML = html;
}

export function mountContainer(theme: WidgetTheme, callbacks: ContainerCallbacks, buttonLogo?: string, aiAvatar?: string): void {
  if (_shadowRoot) return;
  _theme = theme; _locale = theme.locale; _callbacks = callbacks;
  const host = document.createElement("div"); host.id = "onecall-widget-host";
  const shadow = host.attachShadow({ mode: "open" }); _shadowRoot = shadow;
  const pos = theme.position || "bottom-right";
  shadow.innerHTML = '<style>' + generateStyles(theme) + '</style>' +
    '<div class="oc-container ' + pos + '">' +
      '<div class="oc-panel" id="oc-panel">' +
        '<div class="oc-panel-header"><button class="oc-panel-close" id="oc-close">&times;</button><div class="oc-panel-header-top"><button class="oc-panel-back" id="oc-back">' + svgBack() + '</button><div class="oc-panel-title-row"><span class="oc-panel-title-icon">' + (aiAvatar ? '<img src="' + aiAvatar + '" alt="AI" style="width:100%;height:100%;border-radius:8px" />' : svgBot()) + '</span><span class="oc-panel-title">' + (theme.title || "有什么可以帮您？") + '</span></div></div>' +
          '<p class="oc-panel-subtitle"><span class="oc-subtitle-dot"></span>' + (theme.welcomeText || "") + '</p>' +
        '</div>' +
        '<div class="oc-panel-body" id="oc-body"></div>' +
        '<div class="oc-panel-footer">' + t("poweredBy", _locale) + '</div>' +
      '</div>' +
      '<button class="oc-fab pulse" id="oc-fab"><span class="oc-fab-icon oc-fab-icon-chat" style="width:100%;height:100%">' + (buttonLogo ? '<img src="' + buttonLogo + '" alt="chat" style="width:100%;height:100%;border-radius:50%" />' : svgChat()) + '</span><span class="oc-fab-icon oc-fab-icon-close">' + svgX() + '</span></button>' +
    '</div>';
  document.body.appendChild(host);
  const fab = $("#oc-fab"), close = $("#oc-close"), back = $("#oc-back");
  fab?.addEventListener("click", () => { if (_panelOpen) closePanel(); else openPanel(); });
  close?.addEventListener("click", (e) => { e.stopPropagation(); closePanel(); });
  back?.addEventListener("click", (e) => { e.stopPropagation(); _callbacks?.onPanelOpen(); });
  shadow.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest("[data-feature]") as HTMLElement;
    if (!btn) return;
    const feature = btn.getAttribute("data-feature") as Feature;
    const ch = btn.getAttribute("data-ch") || undefined;
    _activeFeature = feature; _panelOpen = false;
    if (_callbacks?.onPanelOpen) _callbacks.onPanelOpen(feature, ch);
    openPanelInternal();
  });
}

function openPanelInternal(): void {
  _panelOpen = true;
  $("#oc-panel")?.classList.add("open");
  const fab = $("#oc-fab");
  fab?.classList.remove("pulse");
  fab?.classList.add("oc-fab-close");
}
function openPanel(): void {
  _panelOpen = true;
  $("#oc-panel")?.classList.add("open");
  const fab = $("#oc-fab");
  fab?.classList.remove("pulse");
  fab?.classList.add("oc-fab-close");
  _callbacks?.onPanelOpen();
}
function closePanel(): void {
  _panelOpen = false;
  $("#oc-panel")?.classList.remove("open");
  const fab = $("#oc-fab");
  fab?.classList.add("pulse");
  fab?.classList.remove("oc-fab-close");
  _callbacks?.onPanelClose();
}

export function setActiveFeature(feature: Feature | null): void {
  _activeFeature = feature;
  const iconEl = $("#oc-badge-icon"), textEl = $("#oc-badge-text");
  if (iconEl && textEl) {
    if (feature) { iconEl.textContent = FEATURE_ICONS[feature]; textEl.textContent = FEATURE_LABELS[feature]; }
    else { iconEl.textContent = "⚠️"; textEl.textContent = "无可用功能"; }
  }
}

export function setPanelContent(html: string): void { const body = $("#oc-body"); if (body) body.innerHTML = html; }
export function setFabPulse(active: boolean): void { const fab = $("#oc-fab"); if (active) fab?.classList.add("pulse"); else fab?.classList.remove("pulse"); }
export function getShadowRoot(): ShadowRoot | null { return _shadowRoot; }

/** 重置容器状态（用于 destroy 后重新 init） */
export function resetContainer(): void {
  _shadowRoot = null; _theme = null; _activeFeature = null; _panelOpen = false;
}

export function showPanelBack(show: boolean): void {
  const btn = $("#oc-back");
  if (btn) btn.style.display = show ? "flex" : "none";
}
export function getTheme(): WidgetTheme | null { return _theme; }
export function getLocale(): Locale { return _locale; }
export function getActiveFeature(): Feature | null { return _activeFeature; }
export function getFeatures(): Feature[] { return _features; }
